// src/pages/RotaGridPage.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import AgencyShiftTable from '@/components/AgencyShiftTable';
import RotaFinanceSummary from '@/components/RotaFinanceSummary';
import OverBudgetModal from '@/components/OverBudgetModal';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Navigate, useParams } from 'react-router-dom';
import { predictTotalCost } from '@/utils/ai/costPredictor';

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minutes = i % 2 === 0 ? '00' : '30';
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
});

const RotaGridPage = () => {
  const { eventId, costCentreId } = useParams();
  const event_id = eventId ? parseInt(eventId) : null;
  const cost_centre_id = costCentreId ? parseInt(costCentreId) : null;

  const [coreShifts, setCoreShifts] = useState({});
  const [agencyShifts, setAgencyShifts] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [agencyDirectory, setAgencyDirectory] = useState([]);
  const [agencyRates, setAgencyRates] = useState([]);
  const [agencyStaff, setAgencyStaff] = useState([]);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [overBudgetModalVisible, setOverBudgetModalVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [showAgency, setShowAgency] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const didFetch = useRef(false);

  const { canViewRota, canEditRota, canManageAgencyShifts } = useUserPermissions();

  const totalCost = useMemo(() => {
    const core = Object.values(coreShifts).reduce((sum, row) => {
      return sum + ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].reduce((dSum, day) => {
        const { start, end } = row[day] || {};
        if (!start || !end) return dSum;
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        const diff = ((eh * 60 + em) - (sh * 60 + sm) + 1440) % 1440;
        return dSum + (diff / 60) * row.pay_rate;
      }, 0);
    }, 0);
    const agency = agencyShifts.reduce((sum, row) => {
      const [sh, sm] = row.start_time?.split(':') || [];
      const [eh, em] = row.end_time?.split(':') || [];
      if (!sh || !eh) return sum;
      const start = parseInt(sh) * 60 + parseInt(sm || 0);
      const end = parseInt(eh) * 60 + parseInt(em || 0);
      const hours = ((end - start + 1440) % 1440) / 60;
      const qty = Math.max(row.quantity || 1, 1);
      return sum + hours * row.hourly_rate * qty;
    }, 0);
    return core + agency;
  }, [coreShifts, agencyShifts]);

  const predictedCost = useMemo(() => predictTotalCost([
    ...Object.values(coreShifts).flatMap(row =>
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
        const shift = row[day];
        if (!shift?.start || !shift?.end) return null;
        return {
          role: row.role,
          start_time: `2024-01-01T${shift.start}:00`,
          end_time: `2024-01-01T${shift.end}:00`,
          hourly_rate: row.pay_rate,
          is_agency: false
        };
      }).filter(Boolean)
    ),
    ...agencyShifts.map(row => ({
      role: row.role,
      start_time: `2024-01-01T${row.start_time}:00`,
      end_time: `2024-01-01T${row.end_time}:00`,
      hourly_rate: row.hourly_rate,
      is_agency: true
    }))
  ]), [coreShifts, agencyShifts]);

  useEffect(() => {
    if (budgetInfo && totalCost > budgetInfo.approved_budget) {
      setOverBudgetModalVisible(true);
    }
  }, [budgetInfo, totalCost]);

  useEffect(() => {
    if (!didFetch.current && (event_id || cost_centre_id)) {
      fetchInitialData();
      didFetch.current = true;
    }
  }, [event_id, cost_centre_id]);

  const fetchInitialData = async () => {
    try {
      const budgetUrl = event_id
        ? `/solo-mdsh-api/staffing/budget-info/event/${event_id}`
        : `/solo-mdsh-api/staffing/budget-info/cost-centre/${cost_centre_id}`;
      const [staffRes, costCentreRes, agencyRes, rateRes, agencyStaffRes] = await Promise.all([
        axios.get('/solo-mdsh-api/staffing/staff-directory'),
        axios.get('/solo-mdsh-api/cost-centres'),
        axios.get('/solo-mdsh-api/staffing/agencies'),
        axios.get('/solo-mdsh-api/staffing/agency-rates'),
        axios.get('/solo-mdsh-api/staffing/agency-assigned-staff')
      ]);
      let budgetRes = null;
      try {
        budgetRes = await axios.get(budgetUrl);
      } catch {}

      setStaffList(staffRes.data?.staff || []);
      setCostCentres(Array.isArray(costCentreRes.data) ? costCentreRes.data : []);
      setAgencyDirectory(agencyRes.data?.agencies || []);
      setAgencyRates(rateRes.data?.data || []);
      setAgencyStaff(agencyStaffRes.data?.staff || []);
      setBudgetInfo(budgetRes?.data || null);

      const core = staffRes.data?.staff?.reduce((acc, staff) => {
        acc[staff.staff_id] = {
          staff_id: staff.staff_id,
          name: staff.full_name,
          role: staff.position,
          pay_rate: parseFloat(staff.pay_rate) || 0,
          monday: {}, tuesday: {}, wednesday: {},
          thursday: {}, friday: {}, saturday: {}, sunday: {}
        };
        return acc;
      }, {}) || {};
      setCoreShifts(core);
    } catch (err) {
      setToastMessage({ text: 'Error fetching data.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateCoreShift = (staffId, day, key, value) => {
    setCoreShifts(prev => ({
      ...prev,
      [staffId]: {
        ...prev[staffId],
        [day]: {
          ...prev[staffId][day],
          [key]: value
        }
      }
    }));
  };

  const handleSubmit = async () => {
    if (!canEditRota) return;
    setSubmitting(true);
    try {
      const coreShiftEntries = Object.values(coreShifts).flatMap(row =>
        ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
          const { start, end } = row[day] || {};
          if (!start || !end || !row.role || !cost_centre_id) return null;
          return {
            event_id,
            cost_centre_id,
            staff_id: row.staff_id,
            role: row.role,
            start_time: `2024-01-01T${start}:00`,
            end_time: `2024-01-01T${end}:00`,
            hourly_rate: row.pay_rate,
            is_agency: false
          };
        }).filter(Boolean)
      );

      const agencyShiftEntries = agencyShifts.map(row => {
        if (!row.start_time || !row.end_time || !row.role || !row.cost_centre_id) return null;
        return {
          event_id,
          cost_centre_id: row.cost_centre_id,
          role: row.role,
          agency_name: row.agency_name,
          agency_assigned_staff: row.agency_assigned_staff || null,
          start_time: `2024-01-01T${row.start_time}:00`,
          end_time: `2024-01-01T${row.end_time}:00`,
          hourly_rate: row.hourly_rate,
          is_agency: true
        };
      }).filter(Boolean);

      const shifts = [...coreShiftEntries, ...agencyShiftEntries];
      if (shifts.length === 0) {
        setToastMessage({ text: 'No valid shifts to submit.', type: 'error' });
        return;
      }

      const res = await axios.post('/solo-mdsh-api/staffing/rota/bulk-add', { shifts });
      if (res.status === 201) {
        setToastMessage({ text: '✅ Shifts submitted successfully.', type: 'success' });
      }
    } catch (err) {
      console.error('❌ Submit error:', err);
      setToastMessage({ text: '❌ Failed to submit shifts.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!canViewRota || (!event_id && !cost_centre_id)) return <Navigate to="/unauthorized" />;
  if (loading) return <div className="p-4 text-gray-300 bg-gray-900">Loading rota data...</div>;

  return (
    <div className="p-4 bg-gray-900 text-white space-y-4 min-h-screen">
      {toastMessage && (
        <div className={`p-2 rounded ${toastMessage.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toastMessage.text}
        </div>
      )}

      <OverBudgetModal
        visible={overBudgetModalVisible}
        onClose={() => setOverBudgetModalVisible(false)}
        onSubmit={handleSubmit}
        overBudgetItems={[{
          label: 'Total Shift Cost',
          actualCost: totalCost,
          budget: budgetInfo?.approved_budget || 0
        }]}
      />

      {/* Header Buttons */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">Rota Grid Planner</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAgency(prev => !prev)}
            disabled={!canManageAgencyShifts}
            className="bg-yellow-600 px-3 py-1 rounded"
          >
            {showAgency ? 'Hide Agency' : 'Add Agency Shift'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !canEditRota}
            className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded"
          >
            {submitting ? 'Submitting...' : 'Submit All'}
          </button>
        </div>
      </div>

      {/* Core Staff */}
      <div className="bg-gray-800 text-white rounded p-4 shadow space-y-2">
        <h3 className="text-lg font-semibold mb-2">Core Staff Shifts</h3>
        <table className="w-full text-sm border border-gray-600">
          <thead className="bg-gray-700">
            <tr>
              <th className="p-2">Name</th>
              <th>Role</th>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <th key={d} className="px-2">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.values(coreShifts).map(row => (
              <tr key={row.staff_id} className="border-t border-gray-600">
                <td className="p-2">{row.name}</td>
                <td>{row.role}</td>
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                  <td key={day}>
                    <div className="flex flex-col gap-1">
                      <select
                        value={row[day]?.start || ''}
                        onChange={e => updateCoreShift(row.staff_id, day, 'start', e.target.value)}
                        className="bg-gray-900 text-white border border-gray-700 rounded px-1 py-0.5"
                      >
                        <option value="">Start</option>
                        {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select
                        value={row[day]?.end || ''}
                        onChange={e => updateCoreShift(row.staff_id, day, 'end', e.target.value)}
                        className="bg-gray-900 text-white border border-gray-700 rounded px-1 py-0.5"
                      >
                        <option value="">End</option>
                        {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Agency Shifts */}
      {showAgency && (
        <AgencyShiftTable
          shifts={agencyShifts}
          agencies={agencyDirectory}
          agencyRates={agencyRates}
          costCentres={costCentres}
          agencyStaff={agencyStaff}
          onChange={(id, updated) => setAgencyShifts(prev =>
            prev.map(row => row.id === id ? updated : row))}
          onDelete={id => setAgencyShifts(prev => prev.filter(row => row.id !== id))}
          addShift={() => setAgencyShifts(prev => [...prev, {
            id: Date.now(),
            role: '',
            quantity: 1,
            agency_name: '',
            cost_centre_id: cost_centre_id || '',
            start_time: '',
            end_time: '',
            time: '',
            hourly_rate: 0,
            agency_assigned_staff: ''
          }])}
        />
      )}

      <RotaFinanceSummary
        totalCost={totalCost}
        approvedBudget={budgetInfo?.approved_budget || 0}
        predictedCost={predictedCost}
      />
    </div>
  );
};

export default RotaGridPage;
